import { type ComponentPropsWithoutRef, type ReactNode, useEffect, useState } from 'react';
import { Button, type ButtonProps } from '@zep-us/zus/button';
import { breakpoints, ICON_COLOR } from '@zep-us/zus/tokens';
import { cn } from '@zep-us/zus/utils';
import { type IconSize, XIcon } from '@zep-us/zus-icons';
import type { PopupActions, ResolvedCampaign } from '../types';
import { toSafeOpenUrl } from './PopupCard/utils';

// 삭제된 quiz-app `module/Notice` 의 PopupNotice UI 를 @zep/crm 로 이관한 공지 스타일
// 캠페인 카드. 우하단(BOTTOM_RIGHT)·모바일 하단 노출용이며 mobile/withImage/withoutImage
// 3종 변형은 원본 공지 팝업과 동일하다.
//
// PopupCard(inline-style, 프레임워크 무관)와 달리 이 카드는 ZEP 디자인시스템
// `@zep-us/zus`(Button·XIcon·토큰·cn·shadow 프리셋) 만으로 구성한다 — 소비 앱이 전부
// ZEP 앱(모두 ZUS + Tailwind 사용)이라는 전제로 포터블 원칙을 개정한 결과. 소비 앱은
// tailwind content 에 `packages/crm/src` 를 포함하고 ZUS preset 을 써야 클래스가 컴파일된다.
// CENTER_MODAL 은 renderNoticePopup 이 기존 PopupCard(CenterModalCard)로 위임하므로
// 여기서는 다루지 않는다(BOTTOM_RIGHT 전용).

// 공지 팝업은 채널톡 런처 등 떠 있는 위젯 위에 보여야 하므로 충분히 높은 z-index 를 준다.
// (quiz 의 z-notice=9999998 을 하드코딩하지 않고 그보다 높은 공통 상수로 둔다.)
const NOTICE_POPUP_Z_INDEX = 10_000_000;

/** 공지 원본과 동일 기준으로 tabletLg 이하를 모바일로 본다. matchMedia 직접 사용(신규 dep 없음). */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoints.tabletLg}px)`);
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);
  return isMobile;
}
interface NoticeCardProps {
  campaign: ResolvedCampaign;
  actions: PopupActions;
}
export function NoticeCard({
  campaign,
  actions
}: NoticeCardProps) {
  const [isActive, setActive] = useState(true);
  const isMobile = useIsMobile();
  const title = campaign.title || '';
  const subText = campaign.body || '';
  const image = campaign.imageUrl || '';
  const renderType = (() => {
    if (isMobile) {
      return 'mobile';
    }
    return image ? 'withImage' : 'withoutImage';
  })();

  // 닫기(X): 서버 dismiss 트래킹 후 캠페인 제거. 공지의 로컬 24h 숨김 대신 CRM 은
  // 서버(maxImpressionPerUser/dismissForToday)가 재노출을 관장한다.
  const onClose = () => {
    setActive(false);
    void actions.trackDismiss().catch(() => undefined);
    actions.close();
  };
  const onTrackClick = () => {
    actions.trackClick();
  };
  if (!isActive) return null;
  if (renderType === 'mobile') {
    return <Container isMobile className={'bg-default'}>
        <CloseIconButton size={'sm'} iconSize={'md'} className={cn('p-0', 'absolute right-[-12px] top-[-16px] w-[36px] ', 'shadow-sm')} onClick={onClose} />
        <div className={'flex flex-col gap-md'}>
          <div className={'flex justify-between gap-md'}>
            <div className={'flex flex-col gap-2xs'}>
              <Title className={'gap-md'}>{title}</Title>
              <Content>{subText}</Content>
            </div>
            {image && <img className={'size-[58px] rounded-sm object-cover object-center'} alt={'notice-img'} src={image} />}
          </div>
          <Buttons campaign={campaign} onTrackClick={onTrackClick} />
        </div>
      </Container>;
  }
  if (renderType === 'withImage') {
    return <Container isMobile={false} className={'p-0'}>
        <div className={'self-stretch rounded-t-md'}>
          <img className={'shrink-0 rounded-t-md'} src={image} alt="notice-img" />
          <CloseIconButton className={'absolute right-[9px] top-[9px] size-[26px] p-3xs'} iconSize={'xs'} onClick={onClose} />
        </div>
        <div className={'flex flex-col gap-[14px] rounded-b-md bg-default p-md'}>
          <div className={'flex flex-col gap-2xs'}>
            <Title>{title}</Title>
            <Content>{subText}</Content>
          </div>
          <Buttons campaign={campaign} onTrackClick={onTrackClick} />
        </div>
      </Container>;
  }
  return <Container isMobile={false} className={'bg-default'} data-sentry-element="Container" data-sentry-component="NoticeCard" data-sentry-source-file="NoticeCard.tsx">
      <div className={'flex flex-col gap-[14px]'}>
        <div className={'flex flex-col gap-2xs'}>
          <Title rightSlot={<CloseIconButton className={'size-[26px] p-3xs'} onClick={onClose} />} data-sentry-element="Title" data-sentry-source-file="NoticeCard.tsx">
            {title}
          </Title>
          <Content data-sentry-element="Content" data-sentry-source-file="NoticeCard.tsx">{subText}</Content>
        </div>
        <Buttons campaign={campaign} onTrackClick={onTrackClick} data-sentry-element="Buttons" data-sentry-source-file="NoticeCard.tsx" />
      </div>
    </Container>;
}
const Container = ({
  isMobile,
  className,
  ...rest
}: ComponentPropsWithoutRef<'div'> & {
  isMobile: boolean;
}) => {
  return <div
  // z-notice(quiz 전용 토큰) 대신 inline z-index — 채널톡 등 떠있는 위젯 위.
  style={{
    zIndex: NOTICE_POPUP_Z_INDEX
  }} className={cn(['animate-once animate-duration-500 animate-delay-[400ms] animate-ease-in ', 'fixed rounded-md border-opacity-assistive border flex flex-col', 'shadow-xl', 'pointer-events-auto', isMobile ? 'bottom-[25px] left-1/2 -translate-x-1/2 w-[calc(100vw-32px)] py-md px-lg' : 'right-md bottom-lg w-[320px] p-md animate-fade-up', className])} {...rest} data-sentry-component="Container" data-sentry-source-file="NoticeCard.tsx" />;
};
const Buttons = ({
  campaign,
  onTrackClick
}: {
  campaign: ResolvedCampaign;
  onTrackClick: () => void;
}) => {
  const buttons = (campaign.buttons ?? []).filter(button => !!button.label && !!button.url).map(button => ({
    text: button.label,
    link: button.url as string
  }))
  // 정렬 반대로 해야함(메인이 우측으로 가야해서 변경됨)
  .reverse();
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.preventDefault();
    const link = buttons[index].link;
    // 서버 캠페인 URL 을 검증 없이 열지 않는다 — CenterModalCard 와 동일하게 http(s)/상대
    // 경로만 허용하고 javascript:/data: 등 위험 스킴은 차단(어드민 변조 대비 P1 보안).
    const safeLink = link ? toSafeOpenUrl(link) : null;
    if (!safeLink) return;
    window.open(safeLink, '_blank', 'noopener,noreferrer');
    onTrackClick();
  };
  if (buttons.length === 0) return null;
  return <div className={'flex gap-2xs'} data-sentry-component="Buttons" data-sentry-source-file="NoticeCard.tsx">
      {buttons.map((button, index, all) => <Button key={index} title={button.text} className={'w-full'} intent={all.length === 2 && index === 0 ? 'neutral' : 'alternative'} size={'md'} onClick={event => handleClick(event, index)}>
          {button.text}
        </Button>)}
    </div>;
};
const Title = (props: ComponentPropsWithoutRef<'h3'> & {
  rightSlot?: ReactNode;
}) => {
  const {
    rightSlot,
    children,
    className,
    ...rest
  } = props;
  return <div className={cn('flex justify-between  gap-3xs text-body-lg-bold text-default', className)} {...rest} data-sentry-component="Title" data-sentry-source-file="NoticeCard.tsx">
      <span>{children}</span>
      {rightSlot}
    </div>;
};
const Content = (props: ComponentPropsWithoutRef<'div'>) => {
  const {
    className,
    ...rest
  } = props;
  return <div className={cn('whitespace-pre-line break-words', 'text-body-sm-medium text-neutral', className)} {...rest} data-sentry-component="Content" data-sentry-source-file="NoticeCard.tsx" />;
};
const CloseIconButton = (props: Pick<ButtonProps, 'intent' | 'size' | 'className' | 'onClick'> & {
  iconSize?: IconSize;
}) => {
  const {
    iconSize,
    ..._props
  } = props;
  return <Button intent={props.intent || 'neutral'} rounded={'full'} size={'2xs'} variant={'contained'} leftSlot={<XIcon fill={ICON_COLOR.neutral} size={iconSize} />} {..._props} data-sentry-element="Button" data-sentry-component="CloseIconButton" data-sentry-source-file="NoticeCard.tsx" />;
};